const SCORABLE_EXERCISE_TYPES = new Set([
  'multiple-choice',
  'select',
  'fill-in',
]);

const EXACT_EVALUATION_MODES = new Set(['exact', 'normalized', 'multi-answer']);

const EXACT_MATCH_NORMALIZATION = Object.freeze({
  collapseWhitespace: true,
  normalizeCase: true,
  normalizeApostrophes: true,
  normalizeHyphenation: false,
  stripArticles: false,
  stripPunctuation: false,
});

const FLEXIBLE_TEXT_NORMALIZATION = Object.freeze({
  collapseWhitespace: true,
  normalizeCase: true,
  normalizeApostrophes: true,
  normalizeHyphenation: true,
  stripArticles: true,
  stripPunctuation: true,
});

const DEFAULT_EVALUATION_MODE_BY_TYPE = Object.freeze({
  'multiple-choice': 'exact',
  select: 'exact',
  'fill-in': 'normalized',
  writing: 'ai-review',
});

const normalizeWhitespace = (value) =>
  String(value ?? '').replace(/\s+/g, ' ');

export const normalizeAnswerValue = (value, options = {}) => {
  const settings = {
    ...EXACT_MATCH_NORMALIZATION,
    ...options,
  };

  let normalized = String(value ?? '').replace(/\u00A0/g, ' ');

  if (settings.normalizeApostrophes) {
    normalized = normalized.replace(/[’`]/g, "'");
  }

  if (settings.normalizeHyphenation) {
    normalized = normalized.replace(/[-‐‑‒–—]/g, ' ');
  }

  if (settings.stripPunctuation) {
    normalized = normalized.replace(/[.,!?;:()[\]{}"']/g, ' ');
  }

  normalized = normalized.trim();

  if (settings.collapseWhitespace) {
    normalized = normalizeWhitespace(normalized);
  }

  if (settings.stripArticles) {
    normalized = normalized.replace(/^(a|an|the)\s+/i, '');
  }

  if (settings.normalizeCase) {
    normalized = normalized.toLowerCase();
  }

  return normalized;
};

export const isQuestionAnswered = (answer) => String(answer ?? '').trim().length > 0;

const normalizeAcceptableAnswer = (value) => String(value ?? '').trim();

const splitLegacyAcceptedAnswers = (answer) =>
  String(answer ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

export const getQuestionEvaluationMode = (exercise = {}, question = {}) => {
  if (question?.evaluationMode) {
    return question.evaluationMode;
  }

  if (exercise?.evaluationMode) {
    return exercise.evaluationMode;
  }

  if (
    exercise?.type === 'fill-in' &&
    Array.isArray(question?.acceptableAnswers) &&
    question.acceptableAnswers.length > 0
  ) {
    return 'multi-answer';
  }

  return DEFAULT_EVALUATION_MODE_BY_TYPE[exercise?.type] || 'exact';
};

export const getQuestionNormalizationOptions = (
  exercise = {},
  question = {},
  evaluationMode = getQuestionEvaluationMode(exercise, question)
) => {
  const defaults =
    evaluationMode === 'exact'
      ? EXACT_MATCH_NORMALIZATION
      : FLEXIBLE_TEXT_NORMALIZATION;

  return {
    ...defaults,
    ...(exercise?.normalization || {}),
    ...(question?.normalization || {}),
  };
};

export const getQuestionAcceptedAnswers = (exercise = {}, question = {}) => {
  const configuredAnswers = Array.isArray(question?.acceptableAnswers)
    ? question.acceptableAnswers
    : [];
  const evaluationMode = getQuestionEvaluationMode(exercise, question);
  const answers = [];

  if (
    evaluationMode === 'multi-answer' &&
    configuredAnswers.length === 0 &&
    typeof question?.answer === 'string' &&
    question.answer.includes(',')
  ) {
    answers.push(...splitLegacyAcceptedAnswers(question.answer));
  } else if (typeof question?.answer === 'string' && question.answer.trim()) {
    answers.push(question.answer);
  }

  answers.push(...configuredAnswers);

  return answers
    .map(normalizeAcceptableAnswer)
    .filter(Boolean)
    .filter(
      (answer, index, collection) =>
        collection.findIndex(
          (candidate) =>
            normalizeAnswerValue(candidate) === normalizeAnswerValue(answer)
        ) === index
    );
};

export const formatAcceptedAnswersForDisplay = (exercise = {}, question = {}) =>
  getQuestionAcceptedAnswers(exercise, question).join(' / ');

export const evaluateQuestionAnswer = ({
  exercise = {},
  question = {},
  userAnswer = '',
}) => {
  const evaluationMode = getQuestionEvaluationMode(exercise, question);
  const acceptedAnswers = getQuestionAcceptedAnswers(exercise, question);
  const isAnswered = isQuestionAnswered(userAnswer);
  const isScorable = isScorableExerciseType(exercise?.type);
  const normalizationOptions = getQuestionNormalizationOptions(
    exercise,
    question,
    evaluationMode
  );
  const normalizedUserAnswer = normalizeAnswerValue(
    userAnswer,
    normalizationOptions
  );
  const matchedAnswer = acceptedAnswers.find(
    (answer) =>
      normalizedUserAnswer === normalizeAnswerValue(answer, normalizationOptions)
  );
  const isCorrect = Boolean(isAnswered && matchedAnswer);
  const needsAiReview =
    evaluationMode === 'ai-verify' && isAnswered && !isCorrect;
  const countsTowardScore =
    isScorable &&
    EXACT_EVALUATION_MODES.has(evaluationMode) &&
    !needsAiReview;

  return {
    acceptedAnswers,
    countsTowardScore,
    evaluationMode,
    isAnswered,
    isCorrect,
    isScorable,
    matchedAnswer: matchedAnswer || null,
    needsAiReview,
  };
};

export const buildEmptyExerciseAnswers = (exercises = []) => {
  const initialAnswers = {};

  exercises.forEach((exercise, idx) => {
    initialAnswers[`exercise-${idx}`] = Array.from(
      { length: exercise?.questions?.length ?? 0 },
      () => ''
    );
  });

  return initialAnswers;
};

export const mergeExerciseAnswersFromStorage = (saved, exercises = []) => {
  const mergedAnswers = {};

  exercises.forEach((exercise, idx) => {
    const key = `exercise-${idx}`;
    const expectedLength = exercise?.questions?.length ?? 0;
    const storedAnswers = saved?.[key];

    if (Array.isArray(storedAnswers) && storedAnswers.length === expectedLength) {
      mergedAnswers[key] = storedAnswers.map((answer) =>
        answer == null ? '' : String(answer)
      );
      return;
    }

    mergedAnswers[key] = Array.from({ length: expectedLength }, () => '');
  });

  return mergedAnswers;
};

export const pickExerciseKeyedFlags = (saved, exercises = []) => {
  const flags = {};

  if (!saved || typeof saved !== 'object') {
    return flags;
  }

  exercises.forEach((_, idx) => {
    const key = `exercise-${idx}`;
    if (saved[key]) {
      flags[key] = true;
    }
  });

  return flags;
};

export const buildIeltsCourseStorageKey = ({
  courseType,
  courseId,
  lessonId,
  journeyActivityId,
  version = 2,
}) => {
  const lessonScope = lessonId || 'default';
  const journeyScope = journeyActivityId || 'standalone';

  return `liwaza:ieltsCourse:v${version}:${courseType}:${courseId}:${lessonScope}:${journeyScope}`;
};

export const isScorableExerciseType = (type) =>
  SCORABLE_EXERCISE_TYPES.has(type);

export const questionHasAnswerKey = (question) =>
  getQuestionAcceptedAnswers({}, question).length > 0;

const hashString = (input) => {
  let hash = 2166136261;

  for (let idx = 0; idx < input.length; idx += 1) {
    hash ^= input.charCodeAt(idx);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

export const getDeterministicShuffledOptions = ({
  courseId,
  exerciseIdx,
  questionIdx,
  options = [],
}) =>
  [...options]
    .map((option, originalIndex) => ({
      option,
      originalIndex,
      sortWeight: hashString(
        `${courseId}:${exerciseIdx}:${questionIdx}:${originalIndex}:${option}`
      ),
    }))
    .sort((left, right) => {
      if (left.sortWeight === right.sortWeight) {
        return left.originalIndex - right.originalIndex;
      }

      return left.sortWeight - right.sortWeight;
    })
    .map(({ option }) => option);

export const getQuestionIntegrityIssues = (question = {}) => {
  const issues = [];
  const options = Array.isArray(question.options) ? question.options : [];

  if (!options.length) {
    return issues;
  }

  const normalizedOptions = options.map(normalizeAnswerValue);
  const hasDuplicateOptions =
    new Set(normalizedOptions).size !== normalizedOptions.length;

  if (hasDuplicateOptions) {
    issues.push('duplicate-options');
  }

  const acceptedAnswers = getQuestionAcceptedAnswers({}, question).map((answer) =>
    normalizeAnswerValue(answer)
  );

  if (
    acceptedAnswers.length > 0 &&
    !acceptedAnswers.some((answer) => normalizedOptions.includes(answer))
  ) {
    issues.push('answer-not-in-options');
  }

  return issues;
};

export const validateIeltsCourseData = (courseData = {}) => {
  const issues = [];
  let optionQuestionCount = 0;
  let firstAnswerCount = 0;

  (courseData.exercises || []).forEach((exercise, exerciseIdx) => {
    (exercise?.questions || []).forEach((question, questionIdx) => {
      const options = Array.isArray(question?.options) ? question.options : [];
      if (!options.length) {
        return;
      }

      optionQuestionCount += 1;
      const acceptedAnswers = getQuestionAcceptedAnswers(exercise, question).map(
        (answer) => normalizeAnswerValue(answer)
      );
      const answerIndex = options.findIndex((option) =>
        acceptedAnswers.includes(normalizeAnswerValue(option))
      );

      if (answerIndex === 0) {
        firstAnswerCount += 1;
      }

      getQuestionIntegrityIssues(question).forEach((issueType) => {
        issues.push({
          type: issueType,
          courseId: courseData.courseId || 'unknown-course',
          exerciseIdx,
          questionIdx,
          title: exercise?.title || `Exercise ${exerciseIdx + 1}`,
        });
      });
    });
  });

  return {
    courseId: courseData.courseId || 'unknown-course',
    issues,
    optionQuestionCount,
    firstAnswerCount,
    firstAnswerRatio:
      optionQuestionCount > 0 ? firstAnswerCount / optionQuestionCount : 0,
  };
};

export const validateIeltsCourseCollection = (
  courseMap = {},
  { firstAnswerBiasThreshold = 0.7 } = {}
) => {
  const reports = Object.values(courseMap)
    .filter(Boolean)
    .map((courseData) => validateIeltsCourseData(courseData));

  const issues = reports.flatMap((report) => report.issues);
  const totalOptionQuestions = reports.reduce(
    (sum, report) => sum + report.optionQuestionCount,
    0
  );
  const totalFirstAnswers = reports.reduce(
    (sum, report) => sum + report.firstAnswerCount,
    0
  );
  const firstAnswerRatio =
    totalOptionQuestions > 0 ? totalFirstAnswers / totalOptionQuestions : 0;

  if (
    totalOptionQuestions > 0 &&
    firstAnswerRatio >= firstAnswerBiasThreshold
  ) {
    issues.push({
      type: 'first-option-bias',
      ratio: firstAnswerRatio,
      totalOptionQuestions,
      totalFirstAnswers,
    });
  }

  return {
    reports,
    issues,
    totalOptionQuestions,
    totalFirstAnswers,
    firstAnswerRatio,
  };
};
